"use client"

import { type ReactNode } from "react"
import { useRouter } from "next/navigation"
import { useAtsSource } from "@/contexts/ats-source-context"
import { invalidateReferenceDataCache } from "@/hooks/use-explorer"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Database, AlertCircle } from "lucide-react"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

/**
 * Persistent ATS source context bar for the source-scoped surfaces (Field Mapping
 * Configuration, the Explorer/Data Pull/Value Mapping tools, and Target Mapping).
 * Lets multi-source tenants switch which source they are viewing/editing without
 * leaving the page; the selection defaults to the active source (or the first source)
 * and is session-local — it does NOT change the tenant's active source. Rendered
 * outside the page's scroll area (see app/page.tsx) so it stays pinned to the top.
 */
export function AtsSourceBar() {
  const router = useRouter()
  const { t } = useTranslation()
  const { sources, selectedSource, setSelectedSourceCode, isLoading, error, hasNoSources } = useAtsSource()

  // Entity details and reference data are cached per ATS system but resolved from the
  // source's credentials, so they belong to the source that was selected when they loaded
  const selectSource = (code: string) => {
    invalidateReferenceDataCache()
    setSelectedSourceCode(code)
  }

  // Single shrink-0 root so the bar keeps its height as the first child of the app's
  // flex column and the content below scrolls beneath it.
  const root = (className: string, children: ReactNode) => (
    <div className={`shrink-0 border-b border-card-border ${className}`}>
      <div className="flex flex-wrap items-center gap-3 px-6 py-1.5">{children}</div>
    </div>
  )

  if (isLoading) {
    return root(
      "bg-muted/40",
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        <Database className="h-4 w-4" /> {t("sourceBar.state.loading")}
      </span>
    )
  }

  if (error) {
    return root(
      "border-destructive/30 bg-destructive/5",
      <span className="flex items-center gap-2 text-sm text-destructive">
        <AlertCircle className="h-4 w-4" /> {t("sourceBar.errors.loadFailed", { error })}
      </span>
    )
  }

  if (hasNoSources) {
    return root(
      "border-fbo-accent/40 bg-fbo-accent/10",
      <>
        <AlertCircle className="h-4 w-4 text-fbo-accent" />
        <span className="text-sm">{t("sourceBar.emptyState.message")}</span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => router.push("?view=ats-sources")}>
          {t("sourceBar.emptyState.action")}
        </Button>
      </>
    )
  }

  const isActiveSelected = selectedSource?.isActive ?? false
  const singleSource = sources.length === 1

  return root(
    "bg-card",
    <>
      <span className="th-section-label flex items-center gap-2">
        <Database className="h-4 w-4 text-primary" /> {t("sourceBar.label")}
      </span>

      {singleSource ? (
        <span className="text-sm">
          {selectedSource?.code}
          {selectedSource?.atsSystem ? <span className="text-muted-foreground"> ({selectedSource.atsSystem})</span> : null}
        </span>
      ) : (
        <Select value={selectedSource?.code ?? ""} onValueChange={selectSource}>
          <SelectTrigger className="h-8 w-[280px]">
            <SelectValue placeholder={t("sourceBar.selectPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {sources.map((source) => (
              <SelectItem key={source.code} value={source.code}>
                {source.code}
                {source.atsSystem ? ` (${source.atsSystem})` : ""}
                {source.isActive ? ` — ${t("sourceBar.active")}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {isActiveSelected ? (
        <Badge variant="secondary" className="gap-1">
          <span className="h-2 w-2 rounded-full bg-fbo-accent" />
          {t("sourceBar.active")}
        </Badge>
      ) : (
        <span className="text-xs text-muted-foreground">{t("sourceBar.nonActiveNote")}</span>
      )}
    </>
  )
}
